// ==================================================
// FILE: themes/archboundEvents.js
// ==================================================

// themes/archbound/archboundEvents.js
// 
// CONTENT LIBRARY - Pure narrative and event data for the Archbound universe
// The game engine draws from these pools to generate and flavorize events
// No logic, no triggers - just structured content templates

/**
 * BACKDROP EVENTS
 * Universe texture and world-building - paint the picture of cosmic horror and ancient mysteries
 * Used by engine for atmospheric events that reinforce setting and lore
 */

export const BACKDROP_EVENTS = [
  {
    id: 'arch_resonance',
    category: 'backdrop',
    title: 'Archway Resonance Detected',
    description: 'Sensors register unusual energy signatures emanating from the primary Archway. The portal pulses with otherworldly intent.',
    narrative: 'The Arch hums with power older than civilization. Something on the other side is stirring.',
    rewards: { intel: 8 },
    tags: ['arch_activity', 'eldritch_presence'],
    storyHooks: []
  },
  {
    id: 'spawn_mutation',
    category: 'backdrop',
    title: 'Spawn Evolution Observed',
    description: 'Xenobiology reports indicate Spawn parasites are adapting at an accelerated rate. They learn from each encounter.',
    narrative: 'The servants of the gods evolve with purpose. Each generation remembers what killed the last.',
    rewards: { intel: 10 },
    tags: ['spawn', 'biological_threat'],
    storyHooks: ['spawn_adaptation']
  },
  {
    id: 'atlantean_whispers',
    category: 'backdrop',
    title: 'Psychic Echoes',
    description: 'Multiple operatives report vivid dreams of Atlantis before its fall. Ancient memories bleeding through the Arch Network.',
    narrative: 'The past refuses to stay buried. Atlantis calls to us from beneath the waves, warning or beckoning—we cannot tell which.',
    rewards: { intel: 5 },
    tags: ['psychic', 'atlantis'],
    storyHooks: []
  },
  {
    id: 'allied_site',
    category: 'backdrop',
    title: 'Allied Arch Site Established',
    description: 'Intelligence confirms another nation has activated a minor Archway. They request coordination and data sharing.',
    narrative: 'We are not alone in this discovery. The question is: will we cooperate or compete in the face of cosmic horror?',
    rewards: { intel: 7, funds: 500 },
    tags: ['international', 'cooperation'],
    storyHooks: ['global_network']
  },
  {
    id: 'artifact_cache',
    category: 'backdrop',
    title: 'Atlantean Artifact Recovered',
    description: 'Excavation team uncovered sealed chamber containing pre-flood technology. Inscriptions reference "the Binding."',
    narrative: 'Atlantis left breadcrumbs for those who would come after. Each artifact is a key, but to what door?',
    rewards: { supplies: 75, intel: 6 },
    tags: ['atlantis', 'technology'],
    storyHooks: ['atlantean_legacy']
  },
  {
    id: 'civilian_panic',
    category: 'backdrop',
    title: 'Public Awareness Incident',
    description: 'Civilian witnesses report seeing "impossible creatures" near the containment perimeter. Media suppression protocols engaged.',
    narrative: 'The veil grows thin. How long can we keep the truth from the world? And what happens when they learn?',
    rewards: { intel: 4 },
    tags: ['secrecy', 'public_relations'],
    storyHooks: []
  },
  {
    id: 'explorer_settlement',
    category: 'backdrop',
    title: 'Beyond-Arch Settlement Located',
    description: 'Recon team discovers human settlement on the other side of the Archway. They claim to be descendants of Atlantean refugees.',
    narrative: 'Humanity exists across the stars, scattered by gods who thought us cattle. But cattle can bite back.',
    rewards: { supplies: 50, intel: 5, funds: 1000 },
    tags: ['multidimensional', 'humanity'],
    storyHooks: ['human_diaspora']
  },
  {
    id: 'sumerian_tablets',
    category: 'backdrop',
    title: 'Sumerian Warnings Deciphered',
    description: 'Linguists translate ancient cuneiform tablets: "When the Gates open, the Sleepers wake. When the Sleepers wake, worlds burn."',
    narrative: 'Every ancient civilization left warnings. All said the same thing: keep the doors closed.',
    rewards: { intel: 12 },
    tags: ['ancient_knowledge', 'prophecy'],
    storyHooks: ['ancient_warnings', 'elder_gods']
  },
  {
    id: 'god_transmission',
    category: 'backdrop',
    title: 'Eldritch Communication',
    description: 'Decoded signal from beyond the Archway: concepts of ownership, harvest, and inevitability. The gods still claim dominion.',
    narrative: 'They speak not in words but in certainties burned into the mind. To them, we were never free.',
    rewards: { intel: 15 },
    tags: ['elder_gods', 'psychological'],
    storyHooks: ['divine_contact']
  },
  {
    id: 'black_market_occult',
    category: 'backdrop',
    title: 'Occult Black Market Emerges',
    description: 'Intelligence reports suggest illegal trade in Atlantean artifacts and Spawn tissue samples. Dangerous knowledge spreading.',
    narrative: 'Where there is power, there are always those foolish enough to grasp it without understanding the cost.',
    rewards: { intel: 6, funds: 1500 },
    tags: ['corruption', 'danger'],
    storyHooks: ['rogue_elements']
  }
];

/**
 * SCHEDULED EVENTS
 * Regular, predictable resource delivery and logistics events
 * Anchor points in the campaign calendar - players come to expect these
 */
export const SCHEDULED_EVENTS = [
  {
    id: 'supply_convoy',
    category: 'scheduled',
    schedulePattern: 'every_7_days',
    title: 'Supply Convoy Arrives',
    description: 'Weekly materiel shipment delivered to the containment site. Command continues to fund the operation.',
    narrative: 'Another week securing the impossible. Command still believes in the mission—or fears what happens if we fail.',
    rewards: { supplies: 50, funds: 5000 },
    tags: ['resources', 'military'],
    storyHooks: []
  },
  {
    id: 'intelligence_briefing',
    category: 'scheduled',
    schedulePattern: 'every_5_days',
    title: 'Intelligence Briefing',
    description: 'Analysis team compiles latest data on Arch activity, Spawn behavior, and global Archway network patterns.',
    narrative: 'Knowledge is our only shield against the unknowable. We catalog horrors to survive them.',
    rewards: { intel: 15 },
    tags: ['intelligence', 'analysis'],
    storyHooks: []
  },
  {
    id: 'research_progress',
    category: 'scheduled',
    schedulePattern: 'every_10_days',
    title: 'Research Milestone',
    description: 'Laboratory team reports breakthrough in understanding Atlantean technology and its interaction with Eldritch energies.',
    narrative: 'We stand on the shoulders of a civilization that failed. Their mistakes teach us how to succeed.',
    rewards: { supplies: 25 },
    tags: ['research', 'atlantis'],
    storyHooks: []
  }
];

/**
 * THREAT EVENTS
 * Escalating danger tied to Arch activity and Eldritch incursions
 * Creates pressure and urgency - makes players feel the cosmic horror closing in
 */
export const THREAT_EVENTS = [
  {
    id: 'spawn_incursion',
    category: 'threat',
    threatIntensity: 'low',
    title: 'Spawn Breach Detected',
    description: 'Multiple Spawn entities detected outside containment protocols. Perimeter teams engaging hostiles.',
    narrative: 'The parasites slip through. They seek hosts, knowledge, and a foothold in our reality.',
    threat: true,
    tags: ['spawn', 'combat'],
    storyHooks: []
  },
  {
    id: 'arch_expansion',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Archway Destabilization',
    description: 'Primary Archway shows signs of dimensional instability. Additional portals may be forming spontaneously.',
    narrative: 'The barrier between worlds grows thin. One door becomes many, and what waits beyond hungers.',
    threat: true,
    tags: ['dimensional', 'escalation'],
    storyHooks: ['portal_multiplication']
  },
  {
    id: 'psychic_assault',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Mass Psychic Event',
    description: 'Entire research wing reports identical nightmares and waking visions. Something is reaching through their minds.',
    narrative: 'The gods need no armies when they can command from within. Our thoughts betray us to powers we cannot comprehend.',
    threat: true,
    tags: ['psychic', 'eldritch'],
    storyHooks: ['mental_invasion']
  },
  {
    id: 'equipment_corruption',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Reality Anchor Failure',
    description: 'Critical stabilization equipment malfunctioning. Sensors detect impossible geometries forming in affected areas.',
    narrative: 'Reality itself rebels against our instruments. Or perhaps something makes it rebel.',
    cost: { supplies: 15 },
    tags: ['dimensional', 'equipment'],
    storyHooks: ['reality_breakdown']
  },
  {
    id: 'cultist_infiltration',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Cult Infiltration Discovered',
    description: 'Security uncovers operatives who worship the Eldritch gods. They seek to "open the way" for their masters\' return.',
    narrative: 'Some humans see gods and fall to their knees. Some see gods and beg to be slaves again.',
    threat: true,
    tags: ['cultists', 'betrayal'],
    storyHooks: ['cult_activity']
  },
  {
    id: 'morale_breakdown',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Existential Crisis',
    description: 'Multiple operatives request psychological discharge. Exposure to cosmic truths breaking human sanity thresholds.',
    narrative: 'Some knowledge cannot be unlearned. Some truths shatter the mind that holds them.',
    threat: true,
    tags: ['psychological', 'sanity'],
    storyHooks: ['sanity_crisis']
  },
  {
    id: 'resource_contamination',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Supply Contamination',
    description: 'Routine inspection reveals Eldritch corruption in supply shipments. Origin unknown. Containment protocols compromised.',
    narrative: 'The rot spreads through our lifelines. What we thought was sustenance carried infection.',
    cost: { supplies: 30 },
    tags: ['corruption', 'supplies'],
    storyHooks: ['systemic_corruption']
  }
];

/**
 * MORALE EVENTS
 * Positive moments, discoveries, breakthroughs
 * Emotional texture that reminds us humanity can prevail - rare but impactful
 */
export const MORALE_EVENTS = [
  {
    id: 'expedition_success',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Successful Expedition',
    description: 'Operatives return from beyond the Archway with valuable intelligence and zero casualties. Protocol refinements working.',
    narrative: 'We venture into impossible places and return unchanged. We are learning to survive the impossible.',
    bonus: true,
    rewards: { funds: 500 },
    tags: ['victory', 'confidence'],
    storyHooks: []
  },
  {
    id: 'atlantean_hero',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Atlantean Records Found',
    description: 'Recovered texts detail the Atlantean uprising. Proof that humans once fought the gods—and nearly won.',
    narrative: 'Our ancestors stood against infinity. They failed, but they fought. Their defiance echoes through millennia.',
    bonus: true,
    rewards: { intel: 5, funds: 1000 },
    tags: ['history', 'inspiration'],
    storyHooks: ['atlantean_rebellion']
  },
  {
    id: 'containment_breakthrough',
    category: 'morale',
    moraleType: 'major_victory',
    title: 'Containment Protocol Success',
    description: 'Research team develops reliable method to neutralize Spawn entities. First reproducible defense against Eldritch biology.',
    narrative: 'We have weaponized knowledge against the unknowable. The servants of gods can bleed.',
    bonus: true,
    rewards: { intel: 20, supplies: 40 },
    tags: ['research', 'breakthrough'],
    storyHooks: ['spawn_weapon']
  },
  {
    id: 'international_alliance',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Global Arch Coalition Formed',
    description: 'Nations with active Archway sites agree to share research and coordinate containment. Humanity unified by cosmic threat.',
    narrative: 'When faced with extinction from beyond the stars, borders become meaningless. We are all human now.',
    bonus: true,
    rewards: { intel: 10, funds: 2000 },
    tags: ['cooperation', 'unity'],
    storyHooks: ['global_network']
  },
  {
    id: 'dimensional_rescue',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Stranded Team Recovered',
    description: 'Extraction team successfully retrieves operatives trapped beyond the Archway for three weeks. All survivors accounted for.',
    narrative: 'We leave no one behind—not even on the other side of reality. This is what separates us from the gods.',
    bonus: true,
    rewards: { supplies: 30, funds: 750 },
    tags: ['rescue', 'loyalty'],
    storyHooks: ['dimensional_rescue']
  },
  {
    id: 'divine_weakness',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Eldritch Vulnerability Identified',
    description: 'Analysis of ancient texts reveals fundamental limitation in the gods\' power. They are not omnipotent—they can be fought.',
    narrative: 'The infinite can be measured. The eternal can end. Even gods have weaknesses, and we have found one.',
    bonus: true,
    rewards: { intel: 25 },
    tags: ['discovery', 'hope'],
    storyHooks: ['divine_weakness', 'final_strike']
  }
];

/**
 * FACTION EVENTS
 * Interactions with international operations, cults, rogue scientists, allied bases
 * Adds political and ideological dimension to the conflict
 */
export const FACTION_EVENTS = [
  {
    id: 'nation_demands',
    category: 'faction',
    title: 'International Pressure',
    description: 'Foreign government demands access to Archway research or threatens to activate their own portal unilaterally.',
    narrative: 'Nations compete even as extinction looms. Pride and power matter more than survival to some.',
    tags: ['politics', 'tension'],
    storyHooks: ['international_conflict']
  },
  {
    id: 'research_partnership',
    category: 'faction',
    title: 'Scientific Alliance Proposed',
    description: 'Allied research facility proposes joint study of Spawn biology and Atlantean artifacts. Shared knowledge could accelerate progress.',
    narrative: 'Science transcends nationality when facing the incomprehensible. Knowledge shared is knowledge multiplied.',
    bonus: true,
    tags: ['cooperation', 'research'],
    storyHooks: ['global_network']
  },
  {
    id: 'cultist_sabotage',
    category: 'faction',
    title: 'Cult Sabotage Attempt',
    description: 'Eldritch worshippers infiltrate the facility and attempt to disable reality anchors. Containment nearly breached.',
    narrative: 'There are those who would open all the doors and welcome the gods back. They are as dangerous as the gods themselves.',
    threat: true,
    tags: ['cultists', 'sabotage'],
    storyHooks: ['cult_activity']
  },
  {
    id: 'base_merger',
    category: 'faction',
    title: 'Arch Site Integration',
    description: 'Smaller international Arch facility requests integration into our command structure. They bring unique data and personnel.',
    narrative: 'The network grows. Each new node strengthens our understanding and our chances of survival.',
    bonus: true,
    rewards: { intel: 15, supplies: 50 },
    tags: ['expansion', 'cooperation'],
    storyHooks: ['global_network']
  }
];

/**
 * DISCOVERY EVENTS
 * Finding artifacts, texts, or dimensional anomalies that change understanding
 * Feeds into larger story arcs and provides hooks for narrative development
 */
export const DISCOVERY_EVENTS = [
  {
    id: 'atlantean_library',
    category: 'discovery',
    title: 'Atlantean Archive Recovered',
    description: 'Expedition discovers intact library from before the flood. Complete historical records of humanity\'s first war with the gods.',
    narrative: 'The victors write history, but the defeated left warnings. Atlantis speaks from beneath the waves.',
    rewards: { intel: 20 },
    tags: ['atlantis', 'history'],
    storyHooks: ['atlantean_rebellion', 'ancient_war']
  },
  {
    id: 'binding_ritual',
    category: 'discovery',
    title: 'The Binding Protocols',
    description: 'Ancient papyrus details ritual used by Atlanteans to temporarily sever Eldritch gods from our dimension.',
    narrative: 'They learned to bind infinity. The knowledge was lost when Atlantis fell. Until now.',
    rewards: { intel: 25 },
    tags: ['ritual', 'power'],
    storyHooks: ['atlantean_legacy', 'divine_binding']
  },
  {
    id: 'egyptian_gateway',
    category: 'discovery',
    title: 'Hidden Egyptian Archway',
    description: 'Archaeological team uncovers sealed chamber beneath Giza. Contains dormant minor Archway buried by ancient priests.',
    narrative: 'Egypt knew. They buried the gates and prayed we would never find them. They failed.',
    bonus: true,
    rewards: { intel: 30 },
    tags: ['archaeology', 'expansion'],
    storyHooks: ['global_network', 'ancient_sites']
  },
  {
    id: 'god_location',
    category: 'discovery',
    title: 'Elder God Coordinates',
    description: 'Through dimensional mapping, we\'ve triangulated the physical location of one of the sleeping Eldritch deities.',
    narrative: 'The hunter becomes the hunted. We know where one of them sleeps. The question is: do we dare strike?',
    bonus: true,
    rewards: { intel: 40 },
    tags: ['strategic', 'offensive'],
    storyHooks: ['final_strike', 'god_hunt']
  }
];

/**
 * CAMPAIGN EVENTS
 * Major turning points, campaign-wide developments, meta-narrative shifts
 * Used sparingly - these change the nature of the cosmic war itself
 */
export const CAMPAIGN_EVENTS = [
  {
    id: 'divine_ultimatum',
    category: 'campaign',
    title: 'The Gods\' Offer',
    description: 'Psychic transmission reaches all Arch sites simultaneously: serve the gods willingly or be harvested. They offer one choice.',
    narrative: 'The cosmic intelligence speaks. It offers us what it believes is mercy. To them, slavery is kindness.',
    threat: true,
    tags: ['moral_choice', 'existential'],
    storyHooks: ['divine_contact', 'final_choice']
  },
  {
    id: 'global_incursion',
    category: 'campaign',
    title: 'Worldwide Arch Activation',
    description: 'All known Archways activate simultaneously. Spawn entities emerge globally. The containment has failed.',
    narrative: 'The doors stand open. Every buried gate, every hidden portal—all awaken as one. The harvest begins.',
    threat: true,
    tags: ['catastrophe', 'invasion'],
    storyHooks: ['final_war']
  },
  {
    id: 'god_sleep_ends',
    category: 'campaign',
    title: 'The Sleeper Wakes',
    description: 'Dimensional sensors detect massive reality distortion. One of the Elder Gods is awakening after millennia of dormancy.',
    narrative: 'That which slept now stirs. The attention of infinity turns toward us. We are noticed.',
    threat: true,
    tags: ['apocalyptic', 'elder_gods'],
    storyHooks: ['divine_awakening', 'final_battle']
  },
  {
    id: 'atlantean_weapon',
    category: 'campaign',
    title: 'Atlantean Superweapon Located',
    description: 'Deep sea expedition discovers the weapon Atlantis built to kill gods. It still functions. It\'s waiting to be used.',
    narrative: 'The ancestors left us more than warnings—they left us a sword. The question is whether we\'re worthy to wield it.',
    bonus: true,
    tags: ['discovery', 'power'],
    storyHooks: ['atlantean_legacy', 'god_weapon']
  },
  {
    id: 'humanity_united',
    category: 'campaign',
    title: 'The Grand Alliance',
    description: 'Every nation with an Arch site unifies under single command. Humanity stands together for the first time in history.',
    narrative: 'Borders dissolve before the infinite. We are not nations—we are humanity. And humanity will not kneel.',
    bonus: true,
    rewards: { intel: 50, funds: 10000 },
    tags: ['unity', 'climax'],
    storyHooks: ['global_network', 'final_offensive']
  }
];

const archboundEventsLibrary = {
  BACKDROP_EVENTS,
  SCHEDULED_EVENTS,
  THREAT_EVENTS,
  MORALE_EVENTS,
  FACTION_EVENTS,
  DISCOVERY_EVENTS,
  CAMPAIGN_EVENTS
};

export default archboundEventsLibrary;